import { useState, useEffect, useMemo, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ChevronDown, Venus, HeartPulse, ClipboardList, Stethoscope, Baby } from 'lucide-react';
import { supabase } from 'utils/supabase';

export default function CheckGroupMaterias({
  options = null,
  onChange,
  modo,
  defaultSelectAll = false,
  accentColor = '#6366f1',
  accentBorderColor = '#a5b4fc',
  accentHoverBgColor = '#eef2ff',
}) {
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [checked, setChecked] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);

  // memoize option lists so we can safely use them in the effect before any early returns
  const optionsList = useMemo(
    () => specialties.map((specialty) => ({ label: specialty.name, value: specialty.id, section: specialty.section })),
    [specialties]
  );

  const defaults = useMemo(() => options || optionsList, [options, optionsList]);

  // Consolidated effect: fetch specialties if needed (when no `options` provided) and
  // initialize `checked` based on the current defaults. This single effect runs
  // on mount and whenever `options` or `specialties.length` change.
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      let localSpecialties = specialties;

      // If no explicit options were passed and we have no specialties loaded -> fetch
      if (!options && specialties.length === 0) {
        try {
          setLoading(true);
          setErrorMessage('');

          const { data, error } = await supabase
          .from('specialties')
          .select('id, name, description, section')
          .order('name', { ascending: true });

          if (error) throw error;

          localSpecialties = data || [];
          if (mounted) setSpecialties(localSpecialties);
        } catch (error) {
          console.error('Error loading specialties:', error);
          if (mounted) setErrorMessage('No se pudieron cargar las especialidades.');
        } finally {
          if (mounted) setLoading(false);
        }
      }

      // compute the defaults from either provided options or the (possibly fetched)
      // specialties list, then initialize the checked array
      const computedDefaults = options || localSpecialties.map((s) => ({ label: s.name, value: s.id }));
      const nextChecked = defaultSelectAll
        ? computedDefaults.map(() => true)
        : computedDefaults.map(d => !!d.defaultChecked);

      if (mounted) {
        setChecked(nextChecked);
        onChange?.(
          computedDefaults
            .map((opt, index) => (nextChecked[index] ? opt.value : null))
            .filter((value) => value !== null)
        );
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [defaultSelectAll, onChange, options, specialties]);

  // Effect to handle modo prop changes
  const selectAll = useCallback(() => {
    setChecked(Array(defaults.length).fill(true));
    const allValues = defaults.map((opt) => opt.value);
    onChange?.(allValues);
  }, [defaults, onChange]);

  const clearAll = useCallback(() => {
    setChecked(Array(defaults.length).fill(false));
    onChange?.([]);
  }, [defaults, onChange]);

  useEffect(() => {
    if (modo === 'enarm' || modo === 'aleatorio') {
      selectAll();
    }
  }, [modo, defaults, selectAll]);

  const toggle = (index) => {
    setChecked(prev => {
      const next = [...prev];
      next[index] = !next[index];
      const selectedValues = defaults
        .map((opt, idx) => (next[idx] ? opt.value : null))
        .filter(val => val !== null);
      onChange?.(selectedValues);
      return next;
    });
  };

  // Group specialties by section
  const groupedBySection = useMemo(() => {
    const groups = {};
    defaults.forEach((opt, idx) => {
      const section = opt.section || 'Sin sección';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push({ ...opt, index: idx });
    });
    return groups;
  }, [defaults]);

  // Handle section checkbox toggle
  const toggleSection = (section) => {
    const sectionItems = groupedBySection[section];
    const allChecked = sectionItems.every((item) => checked[item.index]);

    setChecked(prev => {
      const next = [...prev];
      sectionItems.forEach((item) => {
        next[item.index] = !allChecked;
      });

      const selectedValues = defaults
        .map((opt, idx) => (next[idx] ? opt.value : null))
        .filter(val => val !== null);
      onChange?.(selectedValues);
      return next;
    });
  };

  // Check if all items in a section are selected
  const isSectionFullyChecked = (section) => {
    const sectionItems = groupedBySection[section];
    return sectionItems.length > 0 && sectionItems.every((item) => checked[item.index]);
  };

  // Check if some items in a section are selected
  const isSectionPartiallyChecked = (section) => {
    const sectionItems = groupedBySection[section];
    const checkedCount = sectionItems.filter((item) => checked[item.index]).length;
    return checkedCount > 0 && checkedCount < sectionItems.length;
  };

  // Map sections to icons
  const sectionIconMap = {
    'Interna': <Stethoscope size={20} />,
    'Cirugia': <HeartPulse size={20} />,
    'Pediatria': <Baby size={20} />,
    'Gineco-Obstetricia': <Venus size={20} />,
  };

  const getSectionIcon = (section) => {
    return sectionIconMap[section] || <ClipboardList size={20} />;
  };

  const handleAccordionChange = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return <p>Cargando...</p>;
  }

  if (errorMessage) {
    return <p>Error al cargar las materias: {errorMessage}</p>;
  }

  const allSelected = defaults.length > 0 && checked.length === defaults.length && checked.every(Boolean);
  const selectedCount = checked.filter(Boolean).length;

  return (
    <>
      <h3 className="text-sm font-semibold mb-2">Selecciona materias</h3>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1, alignItems: 'stretch' }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={allSelected ? clearAll : selectAll}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            color: accentColor,
            borderColor: accentBorderColor,
            '&:hover': {
              borderColor: accentColor,
              backgroundColor: accentHoverBgColor,
            },
          }}
          disabled={modo === 'enarm' || modo === 'aleatorio'}
        >
          {allSelected ? 'Quitar todas' : 'Seleccionar todas'}
        </Button>
        <Typography sx={{ alignSelf: 'center', color: '#64748b', fontSize: 12, fontWeight: 500 }}>
          {selectedCount} de {defaults.length} seleccionadas
        </Typography>
      </Stack>

      <Box sx={{ mt: 2 }}>
        {Object.keys(groupedBySection).map((section) => (
          <Accordion
            key={section}
            expanded={expandedSection === section}
            onChange={() => handleAccordionChange(section)}
            sx={{
              border: '1px solid #e2e8f0',
              outline: 'none !important',
              '&:focus, &:focus-visible': {
                outline: 'none !important',
              },
              '&.Mui-expanded': {
                border: `2px solid ${accentColor} !important`,
              },
              '& .MuiAccordionSummary-root, & .MuiAccordionSummary-root:focus, & .MuiAccordionSummary-root:focus-visible': {
                outline: 'none !important',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDown size={24} />}
              aria-controls={`${section}-content`}
              id={`${section}-header`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& .MuiAccordionSummary-content': {
                  minWidth: 0,
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginLeft: 'auto',
                  flexShrink: 0,
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSectionFullyChecked(section)}
                    indeterminate={isSectionPartiallyChecked(section)}
                    onChange={() => toggleSection(section)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={modo === 'enarm' || modo === 'aleatorio'}
                    sx={{
                      color: accentColor,
                      '&.Mui-checked': { color: accentColor },
                      '&.MuiCheckbox-indeterminate': { color: accentColor },
                      '&.Mui-disabled': { color: '#ccc' }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSectionIcon(section)}
                    <Typography sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
                      {section}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                {groupedBySection[section].map((item) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={item.index} sx={{ display: 'flex' }}>
                    <Box
                      onClick={() => {
                        if (modo === 'enarm' || modo === 'aleatorio') return;
                        toggle(item.index);
                      }}
                      sx={{
                        width: '100%',
                        maxWidth: 240,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        p: 1,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        height: 56,
                        cursor: modo === 'enarm' || modo === 'aleatorio' ? 'default' : 'pointer',
                        '&:hover': modo === 'enarm' || modo === 'aleatorio'
                          ? undefined
                          : { borderColor: accentBorderColor, bgcolor: accentHoverBgColor },
                      }}
                    >
                      <FormControlLabel
                        onClick={(event) => event.stopPropagation()}
                        control={
                          <Checkbox
                            checked={!!checked[item.index]}
                            onChange={() => toggle(item.index)}
                            onClick={(event) => event.stopPropagation()}
                            disabled={modo === 'enarm' || modo === 'aleatorio'}
                            sx={{
                              color: accentColor,
                              '&.Mui-checked': { color: accentColor },
                              '&.Mui-disabled': { color: '#ccc' }
                            }}
                          />
                        }
                        label={
                          <Typography noWrap sx={{ ml: 0.5, fontSize: '0.78rem' }}>
                            {item.label}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </>
  );
}